import {NeedsResponseBadge} from '@/components/_domain/SidebarItemBadges/ConversationsBadge/ConversationsBadge';
import {SidebarItem} from '@/contexts/SidebarContext/SidebarContext';
import {
  BaseSidebarContentProps,
  SidebarCategory,
  SidebarCategoryItemProps,
} from '@/contexts/SidebarContext/types';
import {useUserTypeContext} from '@/contexts/UserTypeContext/UserTypeContext';
import {useGetClientPaymentsEnabledQuery} from '@/generated/graphql';
import {useFeatures} from '@/hooks/useFeatures/useFeatures';
import {SidebarContent} from '@/layout/Sidebar/SidebarContent';
import {InboxIcon} from '@/material-symbols/icons';
import {useUserCommunitySettings} from '@ark/react';
import {
  AccountCircle,
  Bolt,
  Draw,
  Engineering,
  EventAvailable,
  HomeSharp,
  Inventory,
  Key,
  MapsHomeWork,
  RoomService,
  ShoppingCartRounded,
  TaskAlt,
  ViewCompact,
} from '@mui/icons-material';
import {useMemo, useRef} from 'react';
import {
  appointmentsItem,
  eventsItem,
  facilitiesItem,
  marketplaceItem,
  messagesItem,
  newsFeedItem,
  productsItem,
  pulseItem,
} from '../sidebarItems';
import {useConversationSidebarItems} from '../useConversationSidebarItems';

type ConciergeSidebarContentProps = BaseSidebarContentProps & {
  isPinned: boolean;
};

export const useConciergeCategoryItems = () => {
  const [features, featuresLoading] = useFeatures();
  const {data} = useGetClientPaymentsEnabledQuery({
    fetchPolicy: 'cache-and-network',
  });
  const {mergedSettings, loading} = useUserCommunitySettings(['concierge']);
  const paymentsEnabled = data?.getClient?.paymentsEnabled;
  const {hasBlocks, hasBlockSuggestions} = useUserTypeContext();
  const conversationsItems = useConversationSidebarItems();
  const prevRef = useRef(new Map<SidebarCategory, SidebarItem[]>());

  const result = useMemo(() => {
    if (loading || featuresLoading) return null;

    const categoryItemsMap = new Map<SidebarCategory, SidebarItem[]>();

    const communitiesItems: SidebarItem[] = [
      {
        url: '/homes',
        label: 'Homes',
        icon: <HomeSharp fontSize="small" />,
      },
      {
        url: '/contact-profiles',
        label: 'Contacts',
        icon: <AccountCircle fontSize="small" />,
      },
      pulseItem,
    ];

    if (hasBlocks) {
      communitiesItems.push({
        url: '/blocks',
        label: 'Blocks',
        icon: <ViewCompact fontSize="small" />,
      });
    }
    categoryItemsMap.set('communities', communitiesItems);

    if (features.Tasks) {
      const tasksItems: SidebarItem[] = [
        {
          url: '/tasks',
          label: 'Tasks',
          icon: <TaskAlt fontSize="small" />,
        },
      ];
      categoryItemsMap.set('tasks', tasksItems);
    }

    const engagementItems: SidebarItem[] = [];
    if (mergedSettings?.newsfeed) {
      engagementItems.push(newsFeedItem);
    }
    if (mergedSettings?.marketplace) {
      engagementItems.push(marketplaceItem);
    }

    if (mergedSettings?.messages) {
      if (features.ConversationLayout) {
        categoryItemsMap.set('conversations', conversationsItems);
      } else {
        engagementItems.push(messagesItem);
      }
    }

    categoryItemsMap.set('engagement', engagementItems);

    const bookingsItems: SidebarItem[] = [];
    if (mergedSettings?.events) {
      bookingsItems.push(eventsItem);
    }
    if (mergedSettings?.facilities) {
      bookingsItems.push(facilitiesItem);
    }
    if (mergedSettings?.appointments) {
      bookingsItems.push(appointmentsItem);
    }
    categoryItemsMap.set('bookings', bookingsItems);

    const conciergeItems: SidebarItem[] = [
      {
        url: '/packages',
        label: 'Packages',
        icon: <Inventory fontSize="small" />,
      },
      {
        url: '/keys',
        label: 'Keys',
        icon: <Key fontSize="small" />,
      },
      {
        url: '/visitors',
        label: 'Visitors',
        icon: <Draw fontSize="small" />,
      },
    ];
    if (features.Contractors) {
      conciergeItems.push({
        url: '/contractors',
        label: 'Contractors',
        icon: <Engineering fontSize="small" />,
      });
    }
    categoryItemsMap.set('concierge', conciergeItems);

    if (features.CommunityProducts && paymentsEnabled) {
      categoryItemsMap.set('payments', [productsItem]);
    }

    return categoryItemsMap;
  }, [
    features,
    paymentsEnabled,
    mergedSettings,
    loading,
    featuresLoading,
    conversationsItems,
    hasBlocks,
    hasBlockSuggestions,
  ]);

  if (result) prevRef.current = result;
  return prevRef.current;
};

export const ConciergeSidebarContent = (
  props: ConciergeSidebarContentProps,
) => {
  const [features] = useFeatures();
  const {data} = useGetClientPaymentsEnabledQuery({
    fetchPolicy: 'cache-and-network',
  });
  const {mergedSettings, loading} = useUserCommunitySettings(['concierge']);
  const paymentsEnabled = data?.getClient?.paymentsEnabled;

  if (loading) {
    return null;
  }

  const communitiesCategoryItem: SidebarCategoryItemProps = {
    label: 'communities',
    icon: <MapsHomeWork fontSize="small" />,
  };

  const conversationsCategoryItem: SidebarCategoryItemProps = {
    label: 'conversations',
    icon: <InboxIcon opticalSize={20} />,
    url: '/messages',
    BadgeComponent: NeedsResponseBadge,
  };

  const engagementCategoryItem: SidebarCategoryItemProps = {
    label: 'engagement',
    icon: <Bolt fontSize="small" />,
  };

  const bookingsCategoryItem: SidebarCategoryItemProps = {
    label: 'bookings',
    icon: <EventAvailable fontSize="small" />,
  };

  const conciergeCategoryItem: SidebarCategoryItemProps = {
    label: 'concierge',
    icon: <RoomService fontSize="small" />,
  };

  const tasksCategoryItem: SidebarCategoryItemProps = {
    label: 'tasks',
    icon: <TaskAlt fontSize="small" />,
    url: '/tasks',
  };

  const categoryItems: SidebarCategoryItemProps[] = [communitiesCategoryItem];

  if (mergedSettings?.messages && features.ConversationLayout) {
    categoryItems.push(conversationsCategoryItem);
  }

  categoryItems.push(engagementCategoryItem, bookingsCategoryItem);

  if (features.Tasks) {
    categoryItems.push(tasksCategoryItem);
  }

  categoryItems.push(conciergeCategoryItem);

  if (features.CommunityProducts && paymentsEnabled) {
    categoryItems.push({
      label: 'payments',
      icon: <ShoppingCartRounded fontSize="small" />,
    });
  }

  return (
    <SidebarContent
      items={categoryItems}
      onClose={props.onClose}
      isPinned={props.isPinned}
    >
      {props.children}
    </SidebarContent>
  );
};
